export type PurchaseOrderStatus =
  | 'draft'
  | 'submitted'
  | 'transferring'
  | 'transferred'
  | 'transfer_failed';

export type PurchaseLineSource = 'internal' | 'supplier_api' | 'manual';

export type AttributeType = 'attribute1' | 'attribute2' | 'attribute3';

export interface SupplierOption {
  supplierId: number;
  supplierName: string;
}

export interface BrandOption {
  brandId: number;
  brandName: string;
  supplierId: number;
  supplierName: string;
}

export interface AttributeOption {
  code: string;
  number: string;
}

export interface InternalSkuOption {
  skuId: number;
  priceLookupUnitNumber: string;
  europeanArticleNumber: string | null;
  attribute1Code: string | null;
  attribute2Code: string | null;
  attribute3Code: string | null;
  weight: number | null;
}

export interface InternalProductOption {
  productId: number;
  productNumber: string;
  productName: string;
  brandName: string | null;
  skus: InternalSkuOption[];
}

export interface PurchaseLineSku {
  purchaseLineSkuId?: number;
  articleNumber: string;
  pluNo: string;
  attribute1Code?: string | null;
  attribute2Code?: string | null;
  attribute3Code?: string | null;
  ean13?: string | null;
  articleName?: string | null;
  supplierPrice?: number | null;
  priceOrig?: number | null;
  price?: number | null;
  priceOrigDkk?: number | null;
  priceDkk?: number | null;
  weight?: number | null;
  quantity: number;
  deliveryMonth?: string | null;
  sourceSkuId?: number | null;
  externalSkuCode?: string | null;
  readyForTransfer?: boolean;
  missingFields?: string[];
}

export interface PurchaseLineSourceRef {
  importedFromExcel?: boolean;
  productMetadataLocked?: boolean;
  productId?: number | string | null;
  lockedProductName?: string | null;
  lockedBrandName?: string | null;
  [key: string]: unknown;
}

export function hasMissingSkuProductData(sku: PurchaseLineSku): boolean {
  // Only validate SKUs that will actually be ordered/transferred.
  if (!(Number(sku.quantity) > 0)) return false;
  if (
    sku.missingFields?.some((field) =>
      /attribute[123]Code(?:NotInSystem)?$/i.test(field),
    )
  ) {
    return true;
  }
  const hasValidEan =
    typeof sku.ean13 === 'string' &&
    /^(?:\d{8}|\d{12}|\d{13}|\d{14})$/.test(sku.ean13.trim());
  return (
    !sku.articleName?.trim() ||
    !hasValidEan ||
    !(Number(sku.supplierPrice) > 0) ||
    !(Number(sku.priceOrig) > 0) ||
    !(Number(sku.price) > 0) ||
    !(Number(sku.weight) > 0)
  );
}

export interface PurchaseLine {
  purchaseLineId?: number;
  lineIndex: number;
  source: PurchaseLineSource;
  sourceRef?: PurchaseLineSourceRef | null;
  brandName?: string | null;
  articleNumber: string;
  productName: string;
  deliveryMonth?: string | null;
  bulkSupplierPrice?: number | null;
  bulkPriceOrig?: number | null;
  bulkPrice?: number | null;
  bulkPriceOrigDkk?: number | null;
  bulkPriceDkk?: number | null;
  manualPriceColors?: string[];
  readyForTransfer?: boolean;
  missingFields?: string[];
  skus: PurchaseLineSku[];
  collapsed?: boolean;
}

export interface PurchaseOrder {
  purchaseOrderId?: number;
  orderNumber: string;
  supplierId: number;
  supplierName?: string | null;
  /** Leverantörens order-/referensnummer */
  supplierOrderNumber?: string | null;
  currency: string;
  season?: string | null;
  defaultDeliveryMonth: string;
  paymentTermsDays?: number;
  status: PurchaseOrderStatus;
  notes?: string | null;
  readyForTransfer?: boolean;
  missingFieldsSummary?: string[];
  createdAt?: string;
  updatedAt?: string;
  lines: PurchaseLine[];
}

export interface PurchaseOrderListItem {
  purchaseOrderId: number;
  orderNumber: string;
  supplierId: number;
  supplierName: string | null;
  supplierOrderNumber?: string | null;
  currency: string;
  season: string | null;
  defaultDeliveryMonth: string;
  paymentTermsDays?: number;
  status: PurchaseOrderStatus;
  readyForTransfer: boolean;
  missingFieldsSummary?: string[];
  createdAt: string;
  updatedAt: string;
  totalUnits: number;
  totalCost: number;
}

export interface PurchaseOrdersResponse {
  purchaseOrders: PurchaseOrderListItem[];
  currentPage: number;
  totalPages: number;
  totalCount: number;
}

export type PurchaseOrderStatsBucket = 'draft' | 'done' | 'all';

export interface PurchaseOrderStatsResponse {
  bucket: PurchaseOrderStatsBucket;
  filters: {
    season: string | null;
    supplierId: number | null;
    deliveryMonthFrom: string | null;
    deliveryMonthTo: string | null;
  };
  kpis: {
    orderCount: number;
    totalUnits: number;
    totalCost: number;
  };
  byStatus: Array<{
    status: PurchaseOrderStatus;
    orderCount: number;
    totalUnits: number;
    totalCost: number;
  }>;
  byDeliveryMonth: Array<{
    month: string; // YYYY-MM
    orderCount: number;
    totalUnits: number;
    totalCost: number;
  }>;
  paymentsByDueMonth: Array<{
    month: string; // YYYY-MM
    dueAmount: number;
  }>;
  dueOrders: Array<{
    month: string; // YYYY-MM
    purchaseOrderId: number;
    orderNumber: string;
    supplierName: string | null;
    dueAmount: number;
  }>;
  topBrands: Array<{
    brandName: string;
    totalCost: number;
    pct: number;
  }>;
}

export interface PurchaseOrderDeliveryMonthDrilldownResponse {
  month: string; // YYYY-MM
  bucket: PurchaseOrderStatsBucket;
  filters: {
    season: string | null;
    supplierId: number | null;
    deliveryMonthFrom: string | null;
    deliveryMonthTo: string | null;
  };
  orders: Array<{
    purchaseOrderId: number;
    orderNumber: string;
    supplierName: string | null;
    status: PurchaseOrderStatus;
    defaultDeliveryMonth: string; // YYYY-MM
    totalUnits: number;
    totalCost: number;
  }>;
}

// ── Purchase order Excel import ───────────────────────────────────────────────

export type PurchaseOrderImportTemplateId =
  | 'bestseller'
  | 'sports_group_denmark';

export type PurchaseOrderImportMappingField =
  | 'articleNumber'
  | 'brandName'
  | 'productName'
  | 'supplierOrderNumber'
  | 'notes'
  | 'attribute1Code'
  | 'attribute2Code'
  | 'attribute3Code'
  | 'priceOrig'
  | 'campaignPrice'
  | 'supplierPrice'
  | 'priceOrigDkk'
  | 'priceDkk'
  | 'deliveryDate'
  | 'ean13'
  | 'pluNo'
  | 'quantity'
  | 'weight';

export type PurchaseOrderImportMappingValue = string | string[];
export type PurchaseOrderImportMapping = Partial<
  Record<PurchaseOrderImportMappingField, PurchaseOrderImportMappingValue>
>;

export type PurchaseOrderImportWeightMode = 'kgDecimal' | 'gramNumber';

export interface PurchaseOrderImportOptions {
  weightMode?: PurchaseOrderImportWeightMode;
  importPluNo?: boolean;
  missingProductsOnly?: boolean;
}

export type PurchaseOrderImportArticleNumberResolution =
  | { action: 'continue' }
  | { action: 'prefix'; prefix: string };

export type PurchaseOrderImportEanResolution =
  | { action: 'match' }
  | { action: 'new' }; // deprecated: EAN matches are auto-matched server-side

export interface PurchaseOrderImportDecisions {
  // deprecated: kept for backwards compatibility with older servers/clients
  eanResolutions?: Record<string, PurchaseOrderImportEanResolution | undefined>;
  articleNumberResolutions?: Record<
    string,
    PurchaseOrderImportArticleNumberResolution | undefined
  >;
  brandMappings?: Record<string, string | undefined>;
  attributeMappings?: Partial<Record<AttributeType, Record<string, string>>>;
}

export interface PurchaseOrderImportParseResponse {
  columns: string[];
  sampleRows: Record<string, string | null>[];
  detectedTemplate: PurchaseOrderImportTemplateId | null;
}

export interface PurchaseOrderImportValidateResponse {
  blockingIssues: {
    missingBrands: string[];
    missingAttributes: Array<{
      attributeType: 'attribute1' | 'attribute2' | 'attribute3';
      missing: string[];
    }>;
    unresolvedEanMatches: string[];
    unresolvedArticleNumberConflicts: string[];
    styleOverrideConflicts: Array<{
      styleKey: string;
      articleNumbers: string[];
      rowIds: string[];
    }>;
    inconsistentEanRows: Array<{
      ean13: string;
      rowIds: string[];
    }>;
    pluConflicts: Array<{
      pluNo: string;
      rowIds: string[];
      articleNumbers: string[];
      existingProductNumber: string | null;
      reason: 'duplicate_in_import' | 'used_by_other_article';
    }>;
    attributeCodesTooLong: Array<{
      attributeType: 'attribute1' | 'attribute2' | 'attribute3';
      code: string;
      maxLength: number;
      rowIds: string[];
    }>;
    truncatedAttributeMatches: Array<{
      attributeType: 'attribute1' | 'attribute2' | 'attribute3';
      importCode: string;
      matchedExistingCode: string;
      storageLimit: number;
      rowIds: string[];
    }>;
  };
  warnings: string[];
  eanMatches: Array<{
    rowId: string;
    ean13: string;
    import: {
      articleNumber: string | null;
      attribute1Code: string | null;
      attribute2Code: string | null;
      attribute3Code: string | null;
      productName: string | null;
    };
    system: {
      skuId: number;
      productNumber: string;
      productName: string | null;
      brandName: string | null;
      pluNo: string | null;
      attribute1Code: string | null;
      attribute2Code: string | null;
      attribute3Code: string | null;
    };
  }>;
  variantMatches?: Array<{
    rowId: string;
    ean13: string | null;
    import: {
      articleNumber: string | null;
      attribute1Code: string | null;
      attribute2Code: string | null;
      attribute3Code: string | null;
      productName: string | null;
      pluNo: string | null;
    };
    system: {
      skuId: number;
      productNumber: string;
      productName: string | null;
      brandName: string | null;
      pluNo: string | null;
      ean13: string | null;
      attribute1Code: string | null;
      attribute2Code: string | null;
      attribute3Code: string | null;
    };
  }>;
  articleNumberConflicts: Array<{
    articleNumber: string;
    existingSupplierId: number | null;
    existingSupplierName: string | null;
    importSupplierId: number;
  }>;
  normalizedPreview: Array<{
    rowId: string;
    articleNumber: string | null;
    productName: string | null;
    brandName: string | null;
    productMetadataLocked: boolean;
    lockedProductName: string | null;
    lockedBrandName: string | null;
    attribute1Code: string | null;
    attribute2Code: string | null;
    attribute3Code: string | null;
    ean13: string | null;
    quantity: number;
    supplierPrice: number | null;
    priceOrig: number | null;
    campaignPrice: number | null;
    priceOrigDkk: number | null;
    priceDkk: number | null;
    deliveryMonth: string | null;
    weight: number | null;
    supplierOrderNumber: string | null;
    notes: string | null;
    pluNo: string | null;
    pluSource: 'excel' | 'system' | null;
    sourceSkuId: number | null;
  }>;
  missingProducts: Array<{
    rowId: string;
    articleNumber: string | null;
    productName: string | null;
    styleVariantName: string | null;
    brandName: string | null;
    bestsellerBrandName: string | null;
    bestsellerSubBrandName: string | null;
    attribute1Code: string | null;
    attribute2Code: string | null;
    attribute3Code: string | null;
    ean13: string;
    quantity: number;
    supplierPrice: number | null;
    priceOrig: number | null;
    campaignPrice: number | null;
    priceOrigDkk: number | null;
    priceDkk: number | null;
    weight: number | null;
    deliveryMonth: string | null;
    supplierOrderNumber: string | null;
    notes: string | null;
    pluNo: string | null;
    productExistsByArticleNumber: boolean;
    variantExistsByAttributes: boolean;
  }>;
  canCreate: boolean;
}

export function lineUnitTotal(line: PurchaseLine): number {
  return line.skus.reduce((sum, sku) => sum + (Number(sku.quantity) || 0), 0);
}

export function lineCostTotal(line: PurchaseLine): number {
  return line.skus.reduce(
    (sum, sku) =>
      sum + (Number(sku.quantity) || 0) * (Number(sku.supplierPrice) || 0),
    0,
  );
}

export function lineRetailTotal(line: PurchaseLine): number {
  return line.skus.reduce(
    (sum, sku) => sum + (Number(sku.quantity) || 0) * (Number(sku.price) || 0),
    0,
  );
}

export function marginPct(cost: number, retail: number): number | null {
  if (!retail) return null;
  return ((retail - cost) / retail) * 100;
}

/** Markup multiple (common in fashion/sport): retail / cost.
 *  Example: cost 200 (ex moms), retail 600 (ink moms) → 3.0
 */
export function markupMultiple(cost: number, retail: number): number | null {
  if (!(Number(cost) > 0) || !(Number(retail) > 0)) return null;
  return retail / cost;
}

export function contributionPerUnit(cost: number, retail: number): number {
  return (retail || 0) - (cost || 0);
}

export function createEmptySku(
  articleNumber = '',
  articleName = '',
): PurchaseLineSku {
  return {
    articleNumber,
    pluNo: '',
    attribute1Code: null,
    attribute2Code: null,
    attribute3Code: null,
    ean13: '',
    articleName,
    supplierPrice: null,
    priceOrig: null,
    price: null,
    priceOrigDkk: null,
    priceDkk: null,
    weight: null,
    quantity: 0,
    deliveryMonth: null,
    sourceSkuId: null,
    externalSkuCode: null,
  };
}

export function createEmptyLine(
  lineIndex: number,
  defaultDeliveryMonth: string,
): PurchaseLine {
  return {
    lineIndex,
    source: 'manual',
    sourceRef: null,
    brandName: null,
    articleNumber: '',
    productName: '',
    deliveryMonth: defaultDeliveryMonth,
    bulkSupplierPrice: null,
    bulkPriceOrig: null,
    bulkPrice: null,
    bulkPriceOrigDkk: null,
    bulkPriceDkk: null,
    manualPriceColors: [],
    readyForTransfer: false,
    missingFields: [],
    skus: [createEmptySku()],
    collapsed: false,
  };
}
